import React, { useState, useEffect } from 'react';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaGithub } from 'react-icons/fa';
import SummaryApi from '../common/index';

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const res = await fetch(SummaryApi.staff.getAll.url);
      const data = await res.json();
      
      // Filter staff members where displayOnDashboard is true
      const displayMembers = data.filter(staff => staff.displayOnDashboard === true);
      setTeamMembers(displayMembers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16 px-4 sm:px-10 bg-gray-50">
        <div className="container mx-auto text-center">
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </section>
    );
  }

  // Don't render section if no team members to display
  if (teamMembers.length === 0) {
    return null;
  }

  return (
    <section className="py-16 px-4 sm:px-10 bg-gray-50" aria-labelledby="team-heading">
      <div className="container mx-auto">
        <header className="text-center mb-12 px-4 sm:px-28">
          <h2 id="team-heading" className="text-3xl font-bold mb-4 text-gray-800">
            Meet Our Team
          </h2>
          <p className="text-sm sm:text-lg text-gray-600">
            Our dedicated team of professionals brings expertise and passion to every project.
          </p>
        </header>

        <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
          {teamMembers.map((member, index) => (
            <article
              key={member._id || index}
              className="w-full sm:w-72 bg-white rounded-2xl shadow-lg overflow-hidden transition-transform duration-300 hover:transform hover:scale-105"
            >
              {/* Profile Image */}
              <div className="flex justify-center pt-8 pb-4">
                <div className="relative">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={`${member.name} - ${member.designation}`}
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-400 shadow-md"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-300 border-4 border-orange-400 flex items-center justify-center shadow-md">
                      <span className="text-3xl text-gray-600 font-semibold">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Member Info */}
              <div className="px-6 pb-6 text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {member.name}
                </h3>

                {/* Short Description */}
                <p className="text-sm text-gray-600 italic mb-3 min-h-[60px] line-clamp-3">
                  {member.shortDescription || 'Passionate professional dedicated to excellence'}
                </p>

                {/* Designation */}
                <p className="text-sm font-semibold text-gray-800 mb-4">
                  {member.designation || 'Team Member'}
                </p>

                {/* Social Media Icons */}
                <div className="flex justify-center gap-3">
                  {member.socialMedia?.facebook && (
                    <a
                      href={member.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-800 text-white hover:bg-blue-600 transition-colors duration-300"
                      aria-label={`${member.name}'s Facebook`}
                    >
                      <FaFacebookF size={14} />
                    </a>
                  )}
                  {member.socialMedia?.instagram && (
                    <a
                      href={member.socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-800 text-white hover:bg-pink-600 transition-colors duration-300"
                      aria-label={`${member.name}'s Instagram`}
                    >
                      <FaInstagram size={14} />
                    </a>
                  )}
                  {member.socialMedia?.linkedin && (
                    <a
                      href={member.socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-800 text-white hover:bg-blue-700 transition-colors duration-300"
                      aria-label={`${member.name}'s LinkedIn`}
                    >
                      <FaLinkedinIn size={14} />
                    </a>
                  )}
                  {member.socialMedia?.github && (
                    <a
                      href={member.socialMedia.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-800 text-white hover:bg-gray-700 transition-colors duration-300"
                      aria-label={`${member.name}'s GitHub`}
                    >
                      <FaGithub size={14} />
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;